import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Crown, Users, Bed, Check, Calendar as CalendarIcon, User, ClipboardList, Info, Home, CheckCircle, Mail, Copy, Loader2, Edit, Trash2, X, Key, Lock, Settings } from "lucide-react";
import { motion } from "motion/react";
import { useAuth } from "../contexts/AuthContext";
import { useBookingStatus } from "../contexts/BookingStatusContext";
import { toast } from "sonner";
import roomService, { Room, Bed as BedType } from "../services/room.service";
import bookingService, { Booking } from "../services/booking.service";
import adminService, { PaymentDetails } from "../services/admin.service";
import { BookingAccessWizard } from "./booking/BookingAccessWizard";
import { AdminAccessCodeGenerator } from "./AdminAccessCodeGenerator";
import { CONTACT } from "../config/contact";

interface BookingPageProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Standard room definition
const standardRoom = {
  name: "Standard Room",
  price: 800,
  description: "Comfortable student accommodation with all essential amenities",
  features: ["Starlink WiFi", "24/7 Electricity", "Shared Bathroom", "Study Desk", "Wardrobe", "Secure Storage"],
  icon: Bed,
};

interface RoomCardProps {
  room: Room;
  userGender?: string;
  isAdmin: boolean;
  onBook: (roomNumber: number) => void;
  onToggleStatus: (room: Room, open: boolean) => void;
}

function RoomCard({ room, userGender, isAdmin, onBook, onToggleStatus }: RoomCardProps) {
  const total = room.totalBeds;
  const available = room.availableBeds;
  const isFull = available === 0;
  const isClosed = room.status !== "active";

  // Gender restriction: only relevant for an open room with free beds
  let isGenderRestricted = false;
  if (userGender && !isFull && !isClosed) {
    const occupants = room.beds
      .filter(b => b.isOccupied && b.currentBookingId?.userId?.gender)
      .map(b => b.currentBookingId?.userId?.gender);
    if (occupants.length > 0 && occupants.some(g => g !== userGender)) {
      isGenderRestricted = true;
    }
  }

  // Admins can open any room (including full/closed ones) to manage occupants;
  // students are gated by closed/full/gender rules as before.
  const isDisabled = isAdmin ? false : (isClosed || isFull || isGenderRestricted);
  // Inline color: the frozen Tailwind CSS lacks bg-gray-400 for the closed state.
  const dotColorHex = isClosed
    ? "#9ca3af"
    : isFull || isGenderRestricted
      ? "#ef4444"
      : "#22c55e";

  return (
    <motion.div
      whileHover={!isDisabled ? { scale: 1.05 } : {}}
      whileTap={!isDisabled ? { scale: 0.95 } : {}}
    >
      <Card
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        aria-label={`Room ${room.roomNumber}, ${isClosed ? "closed" : isGenderRestricted ? "restricted to opposite gender" : `${available} of ${total} beds available`}`}
        className={`p-3 transition-all duration-300 relative ${isDisabled
          ? "opacity-60 cursor-not-allowed bg-gray-50"
          : "cursor-pointer hover:shadow-lg hover:border-[#d4af37]"
          }`}
        onClick={() => !isDisabled && onBook(room.roomNumber)}
        onKeyDown={(e) => {
          if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onBook(room.roomNumber);
          }
        }}
      >
        {/* Availability Indicator */}
        <div className="absolute top-1 right-1 w-3 h-3 rounded-full" style={{ backgroundColor: dotColorHex }}></div>

        <div className="flex flex-col items-center gap-2">
          <Home className="w-8 h-8 text-[#0f1f3d]" />
          <div className="text-center">
            <div className="font-semibold text-sm">Room {room.roomNumber}</div>
            <div className="text-xs text-gray-600 mt-1">
              {isClosed ? (
                <span className="text-gray-500 font-medium">Closed</span>
              ) : isGenderRestricted ? (
                <span className="text-red-600 font-medium">Opposite Gender</span>
              ) : (
                <span>{available}/{total} beds</span>
              )}
            </div>
          </div>

          {isAdmin && (
            <div
              className="flex items-center gap-1.5 mt-1 pt-2 border-t border-gray-100 w-full justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <Switch
                checked={room.status === "active"}
                onCheckedChange={(checked) => onToggleStatus(room, checked)}
                aria-label={`Toggle availability for room ${room.roomNumber}`}
              />
              <span className="text-gray-500" style={{ fontSize: "10px" }}>
                {room.status === "active" ? "Open" : "Closed"}
              </span>
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
}

export function BookingPage({ open, onOpenChange }: BookingPageProps) {
  const { user, logout, refreshUser } = useAuth();
  const { refresh: refreshBookingStatus } = useBookingStatus();
  const [selectedRoomNumber, setSelectedRoomNumber] = useState<number | null>(null);
  const [selectedBed, setSelectedBed] = useState<number | null>(null);
  const [showRoomDetails, setShowRoomDetails] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [bookingId, setBookingId] = useState<string>("");
  const [showAdminView, setShowAdminView] = useState(false);
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [moveInDate, setMoveInDate] = useState<string>("");
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState<Booking | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [deletingBooking, setDeletingBooking] = useState<Booking | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // Admin: remove an occupant from a specific bed
  const [removingOccupant, setRemovingOccupant] = useState<{
    bedNumber: number;
    // The live booking backing this bed, or null when the bed is a "ghost"
    // (occupied flag set but no booking record) — freed by room + bed number.
    booking: Booking | null;
  } | null>(null);
  const [showRemoveOccupantDialog, setShowRemoveOccupantDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [confirmingPaymentBooking, setConfirmingPaymentBooking] = useState<Booking | null>(null);
  const [cancellationReason, setCancellationReason] = useState<string>("");
  const [showAccessCodeGenerator, setShowAccessCodeGenerator] = useState(false);
  const [myBooking, setMyBooking] = useState<Booking | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Admin-controlled booking availability
  const DEFAULT_CLOSED_MESSAGE =
    "Booking is currently closed. Please check back later or contact the hostel manager.";
  const [bookingEnabled, setBookingEnabled] = useState(true);
  const [bookingClosedMessage, setBookingClosedMessage] = useState(DEFAULT_CLOSED_MESSAGE);
  const [showBookingSettings, setShowBookingSettings] = useState(false);
  const [settingsEnabled, setSettingsEnabled] = useState(true);
  const [settingsMessage, setSettingsMessage] = useState(DEFAULT_CLOSED_MESSAGE);
  const [savingSettings, setSavingSettings] = useState(false);
  // Pricing & payment details (admin-configurable via Hostel Settings)
  const DEFAULT_PRICE_PER_YEAR = 6500;
  const DEFAULT_MOMO_SURCHARGE = 1;
  const DEFAULT_MOVE_IN_DATE = "2026-10-05";
  const [pricePerAcademicYear, setPricePerAcademicYear] = useState<number>(DEFAULT_PRICE_PER_YEAR);
  const [momoSurchargePercentage, setMomoSurchargePercentage] = useState<number>(DEFAULT_MOMO_SURCHARGE);
  const [academicMoveInDate, setAcademicMoveInDate] = useState<string>(DEFAULT_MOVE_IN_DATE);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);

  const isAdmin = user?.role === "admin";
  // Explicitly check for true to handle undefined/null
  const hasBookingAccess = isAdmin || user?.hasBookingAccess === true;

  // Fetch rooms and student's booking when dialog opens
  useEffect(() => {
    if (open) {
      fetchRooms();
      fetchConfig();
      // Admins need the bookings list loaded so occupant identities can be shown
      // (and refreshed) when managing / removing occupants from a room.
      if (isAdmin) {
        fetchBookings();
      }
      // Fetch student's own booking if not admin
      if (!isAdmin && hasBookingAccess) {
        fetchMyBooking();
      }
    }
  }, [open, isAdmin, hasBookingAccess]);

  // Fetch bookings when admin view is shown
  useEffect(() => {
    if (showAdminView && isAdmin) {
      fetchBookings();
    }
  }, [showAdminView, isAdmin]);

  const fetchMyBooking = async () => {
    try {
      const bookings = await bookingService.getAllBookings();
      // Find active booking (not cancelled or completed)
      const activeBooking = bookings.find(
        (b: Booking) => b.status !== 'cancelled' && b.status !== 'completed'
      );
      setMyBooking(activeBooking || null);
    } catch (error: any) {
      toast.error(error.message || "Couldn't load your existing booking. Please try again.");
    }
  };

  const fetchConfig = async () => {
    try {
      const config = await adminService.getConfig();
      setBookingEnabled(config.bookingEnabled);
      setBookingClosedMessage(config.bookingClosedMessage || DEFAULT_CLOSED_MESSAGE);
      if (config.pricePerAcademicYear != null) {
        setPricePerAcademicYear(config.pricePerAcademicYear);
      }
      if (config.momoSurchargePercentage != null) {
        setMomoSurchargePercentage(config.momoSurchargePercentage);
      }
      if (config.academicYearMoveInDate) {
        setAcademicMoveInDate(config.academicYearMoveInDate);
      }
      setPaymentDetails(config.paymentDetails || null);
    } catch (error) {
      // Fail open: if config can't be loaded, keep booking enabled so the site
      // never breaks. Don't surface an error toast to students.
      console.error("Failed to load booking config:", error);
    }
  };

  const openBookingSettings = () => {
    setSettingsEnabled(bookingEnabled);
    setSettingsMessage(bookingClosedMessage);
    setShowBookingSettings(true);
  };

  const handleSaveBookingSettings = async () => {
    setSavingSettings(true);
    try {
      const config = await adminService.updateConfig({
        bookingEnabled: settingsEnabled,
        bookingClosedMessage: settingsMessage,
      });
      setBookingEnabled(config.bookingEnabled);
      setBookingClosedMessage(config.bookingClosedMessage || DEFAULT_CLOSED_MESSAGE);
      // Keep the home-page banner / auth modal in sync without a page reload
      refreshBookingStatus();
      toast.success("Booking settings updated!");
      setShowBookingSettings(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to update booking settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const rooms = await roomService.getAllRooms();
      setAllRooms(rooms);
    } catch (error: any) {
      toast.error(error.message || "Failed to load rooms");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    setBookingsLoading(true);
    try {
      const bookings = await bookingService.getAllBookings();
      setAllBookings(bookings);
    } catch (error: any) {
      toast.error(error.message || "Failed to load bookings");
    } finally {
      setBookingsLoading(false);
    }
  };

  // Admin: open/close an individual room
  const handleToggleRoomStatus = async (room: Room, makeOpen: boolean) => {
    try {
      await roomService.updateRoomStatus(room._id, makeOpen ? "active" : "inactive");
      toast.success(`Room ${room.roomNumber} ${makeOpen ? "opened" : "closed"} for booking`);
      await fetchRooms();
    } catch (error: any) {
      toast.error(error.message || "Failed to update room availability");
    }
  };

  const handleRoomClick = (roomNumber: number) => {
    setSelectedRoomNumber(roomNumber);
    setSelectedBed(null);
    setShowRoomDetails(true);
  };

  const handleBedSelect = async (bedNumber: number) => {
    if (isAdmin) return; // admins manage rooms, they don't book beds
    if (!selectedRoomNumber) return;

    try {
      const isAvailable = await roomService.checkBedAvailability(selectedRoomNumber, bedNumber);
      if (!isAvailable) {
        toast.error("This bed is already booked. Please select another bed.");
        return;
      }
      setSelectedBed(bedNumber);
      // Move-in date is the configured academic-year start. Fall back to tomorrow
      // if it's missing or already in the past (the backend rejects past dates).
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const configured = academicMoveInDate ? new Date(academicMoveInDate) : null;
      if (configured && !isNaN(configured.getTime()) && configured >= today) {
        setMoveInDate(academicMoveInDate);
      } else {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setMoveInDate(tomorrow.toISOString().split('T')[0]);
      }
      setShowConfirmDialog(true);
    } catch (error: any) {
      toast.error(error.message || "Failed to check bed availability");
    }
  };

  const handleBooking = async () => {
    if (isAdmin) return; // admins cannot create bookings
    if (!selectedBed || !selectedRoomNumber) {
      toast.error("Please select a bed");
      return;
    }

    if (!moveInDate) {
      toast.error("Please select a move-in date");
      return;
    }

    // Validate move-in date is not in the past
    const selectedDate = new Date(moveInDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast.error("Move-in date cannot be in the past");
      return;
    }

    if (isSubmitting) return; // guard against double submission

    setIsSubmitting(true);
    try {
      const booking = await bookingService.createBooking({
        roomNumber: selectedRoomNumber,
        bedNumber: selectedBed,
        moveInDate,
      });

      // Set booking ID and show success dialog
      setBookingId(booking.bookingId);
      setShowConfirmDialog(false);
      setShowSuccessDialog(true);

      // Refresh rooms to update availability
      await fetchRooms();

      // Refresh student's booking to show booking details
      if (!isAdmin) {
        await fetchMyBooking();
        // Update hasActiveBooking so nav items + CTA label reflect the booking immediately
        await refreshUser();
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to create booking");
      setShowConfirmDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
    setShowRoomDetails(false);
    setSelectedRoomNumber(null);
    setSelectedBed(null);
    setBookingId("");
    setMoveInDate("");
    onOpenChange(false);
  };

  const handleEditBooking = (booking: Booking) => {
    setEditingBooking(booking);
    setMoveInDate(booking.moveInDate.split('T')[0]);
    setShowEditDialog(true);
  };

  const handleUpdateMoveInDate = async () => {
    if (!editingBooking || !moveInDate || !editingBooking._id) return;

    try {
      await bookingService.updateBooking(editingBooking._id, {
        moveInDate,
      });
      toast.success("Move-in date updated successfully!");
      setShowEditDialog(false);
      setEditingBooking(null);
      setMoveInDate("");
      await fetchBookings();
    } catch (error: any) {
      toast.error(error.message || "Failed to update move-in date");
    }
  };

  const handleCancelBooking = (booking: Booking) => {
    setCancellingBooking(booking);
    setCancellationReason("");
    setShowCancelDialog(true);
  };

  const confirmCancelBooking = async () => {
    if (!cancellingBooking || !cancellingBooking._id) return;

    try {
      await bookingService.cancelBooking(
        cancellingBooking._id,
        cancellationReason || "No reason provided"
      );
      toast.success("Booking cancelled successfully!");
      setShowCancelDialog(false);
      setCancellingBooking(null);
      setCancellationReason("");
      await fetchBookings();
      await fetchRooms(); // Refresh rooms to update availability
    } catch (error: any) {
      toast.error(error.message || "Failed to cancel booking");
    }
  };

  // Resolve a bed to the live booking backing it (from the admin bookings list),
  // or null when the bed is a "ghost" — its occupied flag is set but no active
  // booking record exists (e.g. the booking was deleted outside the app).
  const resolveOccupant = (bed: BedType): Booking | null => {
    const id = bed.currentBookingId?._id;
    if (!id) return null;
    return (
      allBookings.find(
        (b) => b._id === id && b.status !== "cancelled" && b.status !== "completed"
      ) || null
    );
  };

  // Admin: open the confirm dialog to free a given bed. Resolves the bed to a
  // live booking when one exists; otherwise it's a "ghost" bed (occupied flag
  // with no booking record) which we free by room + bed number instead.
  const handleRemoveOccupant = (bed: BedType) => {
    setRemovingOccupant({ bedNumber: bed.bedNumber, booking: resolveOccupant(bed) });
    setCancellationReason("");
    setShowRemoveOccupantDialog(true);
  };

  const confirmRemoveOccupant = async () => {
    if (!removingOccupant || !selectedRoomNumber) return;

    try {
      if (removingOccupant.booking?._id) {
        // Real occupant — cancel the booking (handles refund + notification email).
        await bookingService.cancelBooking(
          removingOccupant.booking._id,
          cancellationReason || "Removed by admin"
        );
      } else {
        // Ghost occupancy — no live booking backs this bed, so force-free it.
        await bookingService.releaseBed(
          selectedRoomNumber,
          removingOccupant.bedNumber,
          cancellationReason || "Bed freed by admin"
        );
      }
      toast.success(
        `Bed freed — Room ${selectedRoomNumber}, Bed #${removingOccupant.bedNumber}`
      );
      setShowRemoveOccupantDialog(false);
      setRemovingOccupant(null);
      setCancellationReason("");
      await fetchRooms(); // refreshes the open Room Details dialog (freed bed)
      await fetchBookings(); // keeps occupant identities / admin list in sync
    } catch (error: any) {
      toast.error(error.message || "Failed to free bed");
    }
  };

  const handleConfirmPayment = (booking: Booking) => {
    setConfirmingPaymentBooking(booking);
    setMoveInDate(booking.moveInDate.split('T')[0]);
    setShowPaymentDialog(true);
  };

  const confirmPayment = async () => {
    if (!confirmingPaymentBooking || !confirmingPaymentBooking._id || !moveInDate) return;

    try {
      await bookingService.confirmPayment(
        confirmingPaymentBooking._id,
        moveInDate
      );
      toast.success("Payment confirmed successfully! Email sent to occupant.");
      setShowPaymentDialog(false);
      setConfirmingPaymentBooking(null);
      setMoveInDate("");
      await fetchBookings();
    } catch (error: any) {
      toast.error(error.message || "Failed to confirm payment");
    }
  };

  const handleDeleteBooking = (booking: Booking) => {
    setDeletingBooking(booking);
    setShowDeleteDialog(true);
  };

  const confirmDeleteBooking = async () => {
    if (!deletingBooking || !deletingBooking._id) return;

    try {
      await bookingService.deleteBooking(deletingBooking._id);
      toast.success("Booking deleted successfully!");
      setShowDeleteDialog(false);
      setDeletingBooking(null);
      await fetchBookings();
      await fetchRooms();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete booking");
    }
  };

  const selectedRoom = allRooms.find((r) => r.roomNumber === selectedRoomNumber);
  const totalCost = pricePerAcademicYear; // flat price per head for the academic year

  // Check if all rooms are fully occupied
  const totalAvailableBeds = allRooms.reduce((sum, room) => sum + room.availableBeds, 0);
  const allRoomsOccupied = allRooms.length > 0 && totalAvailableBeds === 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[95vw] lg:max-w-[1200px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div>
              <DialogTitle className="text-2xl flex items-center gap-2">
                <Crown className="w-6 h-6 text-[#d4af37]" />
                {showAdminView ? "All Bookings" : (myBooking && !isAdmin) ? "Your Booking" : "Select Your Room"}
              </DialogTitle>
              <DialogDescription>
                Welcome back, {user?.name}! {isAdmin && "(Admin)"}
              </DialogDescription>
            </div>
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2 mt-4 pb-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAccessCodeGenerator(true)}
                >
                  <Key className="w-4 h-4 mr-2" />
                  Generate Code
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdminView(!showAdminView)}
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  {showAdminView ? "Book Room" : "View Bookings"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openBookingSettings}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Booking Settings
                  {!bookingEnabled && (
                    <Badge className="ml-2 bg-red-100 text-red-700 border-red-300">Closed</Badge>
                  )}
                </Button>
              </div>
            )}
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
            </div>
          ) : (showAdminView && isAdmin) ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg">All Bookings ({allBookings.length})</h3>
                <Badge className="bg-[#d4af37] text-[#0f1f3d]">Admin View</Badge>
              </div>

              {bookingsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
                </div>
              ) : allBookings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No bookings yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {allBookings.map((booking, idx: number) => (
                    <Card key={idx} className={`p-4 hover:shadow-md transition-shadow ${booking.status === 'cancelled' ? 'opacity-60 bg-gray-50' : ''} ${booking.paymentStatus === 'paid' ? 'bg-green-50 border-green-200' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge className="bg-[#0f1f3d] text-white">
                              Room {booking.roomNumber}
                            </Badge>
                            <Badge variant="outline">
                              Bed #{booking.bedNumber}
                            </Badge>
                            <Badge className="bg-[#d4af37]/10 text-[#d4af37]">
                              {booking.roomType}
                            </Badge>
                            {booking.status === 'cancelled' && (
                              <Badge className="bg-red-100 text-red-700 border-red-300">
                                Cancelled
                              </Badge>
                            )}
                            {booking.paymentStatus === 'paid' && (
                              <Badge className="bg-green-100 text-green-700 border-green-300 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Payment Confirmed
                              </Badge>
                            )}
                            <span className="text-sm text-gray-500">
                              GH₵ {booking.totalCost}
                            </span>
                          </div>
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center gap-2 text-gray-600">
                              <User className="w-4 h-4" />
                              <span>{typeof booking.userId === 'string' ? booking.userId : (booking.userId as any)?.email || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <CalendarIcon className="w-4 h-4" />
                              <span>
                                Move-in: {new Date(booking.moveInDate).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="text-gray-600">
                              Duration: {booking.duration} month(s) • Status: {booking.status}
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <span className="flex items-center gap-1">
                                {booking.paymentStatus === 'paid' ? (
                                  <>
                                    <CheckCircle className="w-3 h-3 text-green-600" />
                                    <span className="text-green-600 font-medium">Payment: Confirmed</span>
                                  </>
                                ) : (
                                  <>
                                    <Info className="w-3 h-3 text-amber-500" />
                                    <span className="text-amber-500">Payment: {booking.paymentStatus === 'partial' ? 'Partial' : 'Pending'}</span>
                                  </>
                                )}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-500 mt-2 pt-2 border-t">
                              <span className="text-xs">Booking ID:</span>
                              <code className="text-xs bg-gray-100 px-2 py-1 rounded text-[#0f1f3d]">
                                {booking.bookingId}
                              </code>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditBooking(booking)}
                              className="h-8"
                              disabled={booking.status === 'cancelled'}
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancelBooking(booking)}
                              className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={booking.status === 'cancelled' || booking.status === 'completed'}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Cancel
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteBooking(booking)}
                              className="h-8 text-red-700 hover:text-red-800 hover:bg-red-100 border-red-300"
                            >
                              <X className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">
                              Booked on
                            </div>
                            <div className="text-xs text-gray-600">
                              {new Date(booking.createdAt || booking.moveInDate).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </motion.div>
          ) : (!bookingEnabled && !isAdmin && !myBooking) ? (
            /* Admin-disabled booking - Show closed message to students without a booking */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="py-8"
            >
              <div className="text-center py-8 space-y-6">
                <div className="mx-auto w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                  <Lock className="w-10 h-10 text-red-600" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-semibold text-red-600">Booking Closed</h3>
                  <p className="text-gray-600 max-w-md mx-auto whitespace-pre-line">
                    {bookingClosedMessage}
                  </p>
                </div>
                <Card className="p-6 max-w-lg mx-auto bg-gradient-to-br from-red-50 to-red-100/50 border-red-200">
                  <p className="text-sm text-gray-500">
                    For inquiries, contact:{" "}
                    <a href={`mailto:${CONTACT.emails.general}`} className="text-[#d4af37] hover:underline">
                      {CONTACT.emails.general}
                    </a>
                  </p>
                </Card>
              </div>
            </motion.div>
          ) : !hasBookingAccess ? (
            /* Access Gating - guided wizard: pay -> upload receipt -> access code */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="py-8"
            >
              <BookingAccessWizard
                price={pricePerAcademicYear}
                paymentDetails={paymentDetails}
                momoSurchargePercentage={momoSurchargePercentage}
                onGranted={refreshUser}
              />
            </motion.div>
          ) : (myBooking && !isAdmin) ? (
            /* Student's Booking Details View */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Booking Status Header */}
              <div className="text-center space-y-2">
                <div className="mx-auto w-16 h-16 rounded-full bg-[#d4af37]/10 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-[#d4af37]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0f1f3d]">Your Booking</h3>
                <p className="text-gray-600">You have already booked a room</p>
              </div>

              {/* Booking Details Card */}
              <Card className="p-6 bg-gradient-to-br from-[#d4af37]/5 to-[#d4af37]/10 border-[#d4af37]/20">
                <div className="space-y-4">
                  {/* Booking ID */}
                  <div className="flex items-center justify-between pb-4 border-b border-[#d4af37]/20">
                    <span className="text-sm text-gray-600">Booking ID</span>
                    <span className="font-mono font-semibold text-[#0f1f3d]">{myBooking.bookingId}</span>
                  </div>

                  {/* Room & Bed */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#0f1f3d] flex items-center justify-center">
                        <Home className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Room</p>
                        <p className="font-semibold">{myBooking.roomNumber}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#0f1f3d] flex items-center justify-center">
                        <Bed className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Bed</p>
                        <p className="font-semibold">{myBooking.bedNumber}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Info Message */}
              <Card className="p-4 bg-blue-50 border-blue-200">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-900">
                    Extra details will be communicated through the announcements.
                  </p>
                </div>
              </Card>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Legend */}
              <Card className="p-4 bg-gradient-to-br from-[#d4af37]/5 to-[#d4af37]/10 border-[#d4af37]/20">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="w-4 h-4 text-[#d4af37]" />
                  <h4 className="text-sm">Room Information</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Bed className="w-4 h-4 text-[#0f1f3d]" />
                    <span>Standard Room - GH₵ {pricePerAcademicYear.toLocaleString()} / academic year</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span>Available</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span>Full</span>
                  </div>
                </div>
              </Card>

              {/* Rooms by Floor */}
              {allRooms.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Home className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No rooms available</p>
                </div>
              ) : allRoomsOccupied ? (
                /* All Rooms Occupied - Booking Closed Message */
                <div className="text-center py-8 space-y-6">
                  <div className="mx-auto w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                    <Lock className="w-10 h-10 text-red-600" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-red-600">Booking Closed</h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                      All rooms in the hostel are currently fully occupied. Booking has been temporarily closed.
                    </p>
                  </div>
                  <Card className="p-6 max-w-lg mx-auto bg-gradient-to-br from-red-50 to-red-100/50 border-red-200">
                    <div className="space-y-4">
                      <div className="flex items-center justify-center gap-2 text-red-700">
                        <Users className="w-5 h-5" />
                        <span className="font-medium">All {allRooms.reduce((sum, room) => sum + room.totalBeds, 0)} beds are occupied</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Please check back later or contact the hostel manager for updates on room availability.
                      </p>
                      <div className="pt-4 border-t border-red-200">
                        <p className="text-sm text-gray-500">
                          For inquiries, contact: <a href="mailto:majestyhostel@gmail.com" className="text-[#d4af37] hover:underline">majestyhostel@gmail.com</a>
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Ground Floor Section */}
                  <div>
                    <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-200">
                      <div className="w-10 h-10 rounded-full bg-[#0f1f3d] flex items-center justify-center">
                        <span className="text-white font-bold text-sm">G</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-[#0f1f3d]">Ground Floor</h3>
                        <p className="text-sm text-gray-500">Rooms 2-9, 19-22 (12 rooms)</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                      {allRooms
                        .filter(room => [2, 3, 4, 5, 6, 7, 8, 9, 19, 20, 21, 22].includes(room.roomNumber))
                        .map((room) => (
                          <RoomCard
                            key={room.roomNumber}
                            room={room}
                            userGender={user?.gender}
                            isAdmin={isAdmin}
                            onBook={handleRoomClick}
                            onToggleStatus={handleToggleRoomStatus}
                          />
                        ))}
                    </div>
                  </div>

                  {/* First Floor Section */}
                  <div>
                    <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-200">
                      <div className="w-10 h-10 rounded-full bg-[#d4af37] flex items-center justify-center">
                        <span className="text-[#0f1f3d] font-bold text-sm">1</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-[#0f1f3d]">First Floor</h3>
                        <p className="text-sm text-gray-500">Rooms 11-18, 23-26 (12 rooms)</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                      {allRooms
                        .filter(room => [11, 12, 13, 14, 15, 16, 17, 18, 23, 24, 25, 26].includes(room.roomNumber))
                        .map((room) => (
                          <RoomCard
                            key={room.roomNumber}
                            room={room}
                            userGender={user?.gender}
                            isAdmin={isAdmin}
                            onBook={handleRoomClick}
                            onToggleStatus={handleToggleRoomStatus}
                          />
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </DialogContent>
      </Dialog>

      {/* Room Details Dialog */}
      {selectedRoom && (
        <Dialog open={showRoomDetails} onOpenChange={setShowRoomDetails}>
          <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Home className="w-5 h-5 text-[#d4af37]" />
                Room {selectedRoom.roomNumber} - {standardRoom.name}
              </DialogTitle>
              <DialogDescription>
                Floor {selectedRoom.floor} • GH₵ {pricePerAcademicYear.toLocaleString()} / academic year
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Bed Selection */}
              <div>
                <h4 className="mb-3">{isAdmin ? "Manage Beds" : "Select Your Bed"}</h4>
                <div className="grid grid-cols-2 gap-4">
                  {selectedRoom.beds.map((bed) => {
                    const bedNumber = bed.bedNumber;
                    const isBooked = bed.isOccupied;
                    // Only students may click an available bed to book it.
                    const isInteractive = !isAdmin && !isBooked;
                    // Resolve the occupant's live booking (admin only — students
                    // don't load the bookings list).
                    const occupantBooking = isAdmin && isBooked ? resolveOccupant(bed) : null;
                    const occupantEmail = occupantBooking && typeof occupantBooking.userId !== "string"
                      ? (occupantBooking.userId as any)?.email
                      : null;
                    // A bed flagged occupied with no live booking behind it is a
                    // "ghost" — surface it distinctly so an admin can clear it.
                    // Guard on !bookingsLoading so a real occupant isn't briefly
                    // shown as a ghost while the bookings list is still loading.
                    const isGhost = isAdmin && isBooked && !bookingsLoading && !occupantBooking;

                    return (
                      <motion.div
                        key={bedNumber}
                        whileHover={isInteractive ? { scale: 1.05 } : {}}
                        whileTap={isInteractive ? { scale: 0.95 } : {}}
                      >
                        <Card
                          role={isInteractive ? "button" : undefined}
                          tabIndex={isInteractive ? 0 : -1}
                          aria-disabled={!isInteractive}
                          aria-label={`Bed ${bedNumber}, ${isBooked ? "occupied" : "available"}`}
                          className={`p-6 border-2 transition-all duration-300 ${isBooked
                            ? "bg-red-500/20 border-red-500"
                            : "bg-green-500/20 border-green-500"
                            } ${isInteractive ? "hover:bg-green-500/30 cursor-pointer" : "cursor-default"}`}
                          onClick={() => isInteractive && handleBedSelect(bedNumber)}
                          onKeyDown={(e) => {
                            if (isInteractive && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              handleBedSelect(bedNumber);
                            }
                          }}
                        >
                          <div className="flex flex-col items-center gap-3">
                            <Bed className={`w-12 h-12 ${isBooked ? "text-red-600" : "text-green-600"
                              }`} />
                            <div className="text-center">
                              <div className={isBooked ? "text-red-600" : "text-green-600"}>
                                Bed #{bedNumber}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {isGhost ? "No booking record" : isBooked ? "Booked" : "Available"}
                              </div>
                              {/* Student view: booking id on an occupied bed */}
                              {isBooked && !isAdmin && bed.currentBookingId && (
                                <div className="mt-2 pt-2 border-t border-red-300">
                                  <div className="text-xs text-gray-500">Booking ID:</div>
                                  <code className="text-xs text-red-700 bg-white px-2 py-1 rounded mt-1 inline-block">
                                    {typeof bed.currentBookingId === 'object' ? (bed.currentBookingId as any)?.bookingId || 'Booked' : bed.currentBookingId}
                                  </code>
                                </div>
                              )}
                              {/* Admin view: occupant identity + remove action */}
                              {isBooked && isAdmin && (
                                <div className="mt-2 pt-2 border-t border-red-300 flex flex-col items-center gap-2">
                                  {occupantEmail ? (
                                    <div className="text-xs text-gray-700 break-all">{occupantEmail}</div>
                                  ) : isGhost ? (
                                    <div className="text-xs text-amber-600">Stale — no occupant</div>
                                  ) : (
                                    <div className="text-xs text-gray-500">Occupied</div>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="h-7 text-xs"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveOccupant(bed);
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3 mr-1" />
                                    {isGhost ? "Clear bed" : "Remove occupant"}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="sm:max-w-[500px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-[#d4af37]" />
              Confirm Your Booking
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to book the following:
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            <Card className="p-4 bg-[#d4af37]/5 border-[#d4af37]/20">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Room</span>
                  <span className="font-medium">Room {selectedRoomNumber}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Room Type</span>
                  <span className="font-medium">{standardRoom.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Bed</span>
                  <span className="font-medium">Bed #{selectedBed}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-[#d4af37]/20">
                  <span className="text-sm text-gray-600">Rent</span>
                  <span className="font-semibold text-[#0f1f3d]">GH₵ {pricePerAcademicYear.toLocaleString()} / academic year</span>
                </div>
              </div>
            </Card>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isSubmitting}
              onClick={() => {
                setSelectedBed(null);
                setShowConfirmDialog(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="gold"
              disabled={isSubmitting}
              onClick={handleBooking}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Booking…
                </>
              ) : (
                "Confirm Booking"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Success Dialog with Booking ID */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-6 h-6" />
              Booking Confirmed!
            </DialogTitle>
            <DialogDescription>
              Your booking has been successfully confirmed. Please save your booking ID for future reference.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Success Icon and Message */}
            <div className="text-center space-y-3">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <p className="text-gray-600">
                Your booking has been successfully confirmed!
              </p>
            </div>

            {/* Booking ID Display */}
            <Card className="p-4 bg-gradient-to-br from-[#d4af37]/10 to-[#c9a429]/10 border-[#d4af37]">
              <div className="text-center space-y-2">
                <p className="text-sm text-gray-600">Your Booking ID</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="text-2xl text-[#0f1f3d] px-4 py-2 bg-white rounded">
                    {bookingId}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        // Use modern Clipboard API
                        if (navigator.clipboard && window.isSecureContext) {
                          await navigator.clipboard.writeText(bookingId);
                          toast.success("Booking ID copied to clipboard!");
                        } else {
                          // Fallback for older browsers or non-secure contexts
                          const textArea = document.createElement("textarea");
                          textArea.value = bookingId;
                          textArea.style.position = "fixed";
                          textArea.style.left = "-9999px";
                          textArea.style.top = "0";
                          document.body.appendChild(textArea);
                          textArea.focus();
                          textArea.select();

                          try {
                            const successful = document.execCommand('copy');
                            if (successful) {
                              toast.success("Booking ID copied to clipboard!");
                            } else {
                              toast.error("Failed to copy. Please copy manually.");
                            }
                          } catch (err) {
                            toast.error("Failed to copy. Please copy manually.");
                          }

                          document.body.removeChild(textArea);
                        }
                      } catch (err) {
                        toast.error("Failed to copy. Please copy manually.");
                      }
                    }}
                    className="h-8 w-8 p-0"
                    title="Copy Booking ID"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>

            {/* Email Confirmation Notice */}
            <div className="flex gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm text-blue-900">
                  A confirmation email will be sent to <span className="font-semibold">{user?.email}</span>
                </p>
                <p className="text-xs text-blue-700">
                  Please check your inbox and spam folder.
                </p>
              </div>
            </div>

            {/* Important Note */}
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-900">
                <span className="font-semibold">Important:</span> Please note down your Booking ID for future reference. You may need it when checking in or for any inquiries.
              </p>
            </div>
          </div>

          <div className="flex justify-center">
            <Button
              onClick={handleCloseSuccessDialog}
              variant="gold"
              className="px-8"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Occupant Dialog (Admin Only) */}
      <AlertDialog open={showRemoveOccupantDialog} onOpenChange={setShowRemoveOccupantDialog}>
        <AlertDialogContent className="sm:max-w-[500px]">
          <AlertDialogHeader>
            <div className="flex items-start justify-between">
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Remove Occupant
              </AlertDialogTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 rounded-full"
                onClick={() => {
                  setShowRemoveOccupantDialog(false);
                  setRemovingOccupant(null);
                  setCancellationReason("");
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <AlertDialogDescription>
              This frees the bed and cancels the occupant's booking. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {removingOccupant && (
            <div className="space-y-4 py-4">
              {/* Occupant Info */}
              <Card className="p-4 bg-red-50 border-red-200">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Room:</span>
                    <span>Room {selectedRoomNumber}, Bed #{removingOccupant.bedNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Occupant:</span>
                    <span className="break-all">
                      {removingOccupant.booking
                        ? (typeof removingOccupant.booking.userId === "string"
                            ? removingOccupant.booking.userId
                            : (removingOccupant.booking.userId as any)?.email || "N/A")
                        : "No booking record (stale bed)"}
                    </span>
                  </div>
                  {removingOccupant.booking && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Booking ID:</span>
                      <code className="text-xs bg-white px-2 py-1 rounded">
                        {removingOccupant.booking.bookingId}
                      </code>
                    </div>
                  )}
                </div>
              </Card>

              {/* Reason */}
              <div className="space-y-2">
                <label htmlFor="removeReason" className="text-sm font-medium">
                  Reason (Optional)
                </label>
                <Textarea
                  id="removeReason"
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="Enter reason for removing this occupant..."
                  rows={3}
                  className="resize-none"
                />
              </div>

              {/* Warning */}
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-900">
                  <span className="font-semibold">Note:</span> The occupant will be notified via email about the cancellation. A refund will be calculated based on the cancellation policy.
                </p>
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowRemoveOccupantDialog(false);
                setRemovingOccupant(null);
                setCancellationReason("");
              }}
            >
              Keep Occupant
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveOccupant}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Remove Occupant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Booking Dialog (Admin Only) */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent className="sm:max-w-[500px]">
          <AlertDialogHeader>
            <div className="flex items-start justify-between">
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Cancel Booking
              </AlertDialogTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 rounded-full"
                onClick={() => {
                  setShowCancelDialog(false);
                  setCancellingBooking(null);
                  setCancellationReason("");
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <AlertDialogDescription>
              Are you sure you want to cancel this booking? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {cancellingBooking && (
            <div className="space-y-4 py-4">
              {/* Booking Info */}
              <Card className="p-4 bg-red-50 border-red-200">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Booking ID:</span>
                    <code className="text-xs bg-white px-2 py-1 rounded">
                      {cancellingBooking.bookingId}
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Room:</span>
                    <span>Room {cancellingBooking.roomNumber}, Bed #{cancellingBooking.bedNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">User:</span>
                    <span>{typeof cancellingBooking.userId === 'string' ? cancellingBooking.userId : (cancellingBooking.userId as any)?.email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total Cost:</span>
                    <span>GH₵ {cancellingBooking.totalCost}</span>
                  </div>
                </div>
              </Card>

              {/* Cancellation Reason */}
              <div className="space-y-2">
                <label htmlFor="cancellationReason" className="text-sm font-medium">
                  Cancellation Reason (Optional)
                </label>
                <Textarea
                  id="cancellationReason"
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="Enter reason for cancellation..."
                  rows={3}
                  className="resize-none"
                />
              </div>

              {/* Warning */}
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-900">
                  <span className="font-semibold">Note:</span> The user will be notified via email about the cancellation. A refund will be calculated based on the cancellation policy.
                </p>
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowCancelDialog(false);
                setCancellingBooking(null);
                setCancellationReason("");
              }}
            >
              Keep Booking
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancelBooking}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Cancel Booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Booking Dialog (Admin Only) */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="sm:max-w-[500px]">
          <AlertDialogHeader>
            <div className="flex items-start justify-between">
              <AlertDialogTitle className="flex items-center gap-2 text-red-700">
                <X className="w-5 h-5" />
                Delete Booking Permanently
              </AlertDialogTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 rounded-full"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeletingBooking(null);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <AlertDialogDescription>
              This will permanently delete this booking from the database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {deletingBooking && (
            <div className="space-y-4 py-4">
              {/* Booking Info */}
              <Card className="p-4 bg-red-50 border-red-200">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Booking ID:</span>
                    <code className="text-xs bg-white px-2 py-1 rounded">
                      {deletingBooking.bookingId}
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Room:</span>
                    <span>Room {deletingBooking.roomNumber}, Bed #{deletingBooking.bedNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">User:</span>
                    <span>{typeof deletingBooking.userId === 'string' ? deletingBooking.userId : (deletingBooking.userId as any)?.email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="capitalize">{deletingBooking.status}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total Cost:</span>
                    <span>GH₵ {deletingBooking.totalCost}</span>
                  </div>
                </div>
              </Card>

              {/* Warning */}
              <div className="p-3 bg-red-100 rounded-lg border border-red-300">
                <p className="text-xs text-red-900">
                  <span className="font-semibold">Warning:</span> This will permanently remove the booking record from the database. The bed will become available again. Use this only if you need to completely remove the booking (e.g., duplicate entries, test data). For normal cancellations, use “Cancel Booking” instead.
                </p>
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowDeleteDialog(false);
                setDeletingBooking(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteBooking}
              className="bg-white hover:bg-red-50 text-red-600 border-red-600 border-2"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Move-in Date Dialog (Admin Only) */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#d4af37]" />
              Edit Move-in Date
            </DialogTitle>
            <DialogDescription>
              Update the move-in date for this booking
            </DialogDescription>
          </DialogHeader>

          {editingBooking && (
            <div className="space-y-4 py-4">
              {/* Booking Info */}
              <Card className="p-4 bg-gray-50">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Booking ID:</span>
                    <code className="text-xs bg-white px-2 py-1 rounded">
                      {editingBooking.bookingId}
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Room:</span>
                    <span>Room {editingBooking.roomNumber}, Bed #{editingBooking.bedNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Current Move-in Date:</span>
                    <span>{new Date(editingBooking.moveInDate).toLocaleDateString()}</span>
                  </div>
                </div>
              </Card>

              {/* Date Input */}
              <div className="space-y-2">
                <label htmlFor="editMoveInDate" className="text-sm font-medium flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  New Move-in Date
                </label>
                <Input
                  id="editMoveInDate"
                  type="date"
                  value={moveInDate}
                  onChange={(e) => setMoveInDate(e.target.value)}
                  required
                />
              </div>

              {/* Warning */}
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-900">
                  <span className="font-semibold">Note:</span> Changing the move-in date will update the booking schedule. Make sure to inform the user about this change.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowEditDialog(false);
                setEditingBooking(null);
                setMoveInDate("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateMoveInDate}
              variant="gold"
              disabled={!moveInDate}
            >
              Update Date
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Dialog (Admin Only) */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Confirm Payment
            </DialogTitle>
            <DialogDescription>
              Confirming payment will mark this booking as paid and send a confirmation email to the occupant.
            </DialogDescription>
          </DialogHeader>

          {confirmingPaymentBooking && (
            <div className="space-y-4 py-4">
              {/* Booking Info */}
              <Card className="p-4 bg-green-50 border-green-200">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Booking ID:</span>
                    <code className="text-xs bg-white px-2 py-1 rounded">
                      {confirmingPaymentBooking.bookingId}
                    </code>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Room:</span>
                    <span>Room {confirmingPaymentBooking.roomNumber}, Bed #{confirmingPaymentBooking.bedNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">User:</span>
                    <span>{typeof confirmingPaymentBooking.userId === 'string' ? confirmingPaymentBooking.userId : (confirmingPaymentBooking.userId as any)?.email || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total Cost:</span>
                    <span className="font-semibold text-green-700">GH₵ {confirmingPaymentBooking.totalCost}</span>
                  </div>
                </div>
              </Card>

              {/* Date Input */}
              <div className="space-y-2">
                <label htmlFor="confirmMoveInDate" className="text-sm font-medium flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  Move-in Date
                </label>
                <Input
                  id="confirmMoveInDate"
                  type="date"
                  value={moveInDate}
                  onChange={(e) => setMoveInDate(e.target.value)}
                  required
                />
                <p className="text-xs text-gray-500">
                  You can update the move-in date if needed.
                </p>
              </div>

              {/* Info */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-900 flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  An email will be sent to the occupant with payment confirmation and move-in instructions.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPaymentDialog(false);
                setConfirmingPaymentBooking(null);
                setMoveInDate("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmPayment}
              variant="gold"
              disabled={!moveInDate}
            >
              Confirm Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Access Code Generator Dialog */}
      <AdminAccessCodeGenerator
        open={showAccessCodeGenerator}
        onOpenChange={setShowAccessCodeGenerator}
      />

      {/* Admin Booking Settings Dialog */}
      <Dialog open={showBookingSettings} onOpenChange={setShowBookingSettings}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#d4af37]" />
              Booking Settings
            </DialogTitle>
            <DialogDescription>
              Control whether students can book rooms and what they see when booking is closed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium text-[#0f1f3d]">Accept new bookings</p>
                <p className="text-sm text-gray-500">
                  {settingsEnabled ? "Students can book rooms." : "Booking is closed for students."}
                </p>
              </div>
              <Switch
                checked={settingsEnabled}
                onCheckedChange={setSettingsEnabled}
              />
            </div>

            {!settingsEnabled && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#0f1f3d]">
                  Message shown to students
                </label>
                <Textarea
                  value={settingsMessage}
                  onChange={(e) => setSettingsMessage(e.target.value)}
                  placeholder={DEFAULT_CLOSED_MESSAGE}
                  rows={4}
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowBookingSettings(false)}
                disabled={savingSettings}
              >
                Cancel
              </Button>
              <Button
                variant="gold"
                onClick={handleSaveBookingSettings}
                disabled={savingSettings}
              >
                {savingSettings ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Settings"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
